/**
 * หน้าจอนำเข้า Forecast ลูกค้า (Forecast Import, P2-5a) — รายการ ค้นหา นำไฟล์เข้า
 * **ดูว่าแถวไหนผิดและเพราะอะไร** และประกาศใช้
 *
 * ปิดทั้งสี่ operation ของ contract ที่ `WO-P2-M5-001` เปิดไว้ (D-093):
 * `GET`/`POST /v1/forecast-imports`, `GET /{batch_id}` และ `POST /{batch_id}/publish`
 *
 * โครงยืมมาจาก `SalesOrderScreen` ทั้งท่อนโดยตั้งใจ — ช่องค้นหาบนสุด ฟอร์มนำเข้าอยู่
 * หลังปุ่มในกล่อง `Modal` ปุ่มรายแถว และหลังสำเร็จก็ **โหลดรายการใหม่ผ่าน client
 * เส้นเดิม** ไม่ใช่รีโหลดทั้งหน้า ความต่างทุกข้อเป็นข้อที่ D-093 เขียนไว้:
 *
 * 1. **`201` ไม่ได้แปลว่าทุกอย่างถูกต้อง** ต่างจากทุกหน้าจอ `POST` อื่นในระบบนี้
 *    การนำเข้าสำเร็จเสมอตราบที่ payload ถูกรูป สิ่งที่ต้องอ่านต่อคือ `status` ของ
 *    batch ที่ได้กลับมา — `VALIDATED` หรือ `INVALID` — และ **บรรทัดที่ผิดถูกเก็บไว้
 *    ครบ** ไม่ได้ถูกทิ้ง (flow ข้อ 1.3)
 * 2. **การแสดงว่าแถวไหนผิดคือเหตุผลที่หน้าจอนี้มีอยู่** ใบที่ `INVALID` เปิดออกมา
 *    เป็นตารางรายบรรทัดที่ทำเครื่องหมายแถวที่ไม่ผ่านพร้อมคำอธิบายภาษาไทย ไม่ใช่
 *    ป้ายเดียวว่า "นำเข้าไม่สำเร็จ" — ป้ายแบบนั้นคืนสิ่งที่กฎข้อ 1.3 เพิ่งกันไว้
 *    กลับไปให้ผู้ใช้ด้วยมืออีกข้าง
 * 3. **สร้างกับประกาศใช้เป็นสองขั้นที่มองเห็นได้** ปุ่ม "ประกาศใช้" ขึ้นเฉพาะแถวที่
 *    `VALIDATED` และถูกกันด้วย `PLANNING` + **APPROVE** ส่วนการนำเข้าใช้ CREATE —
 *    flow ข้อ 1.4 ระบุว่า "ผู้รับผิดชอบตรวจและ Publish" หน้าจอที่ทำสองอย่างในคลิก
 *    เดียวคือหน้าจอที่ลบขั้นตอนการตรวจนั้นทิ้ง
 * 4. **ใบที่ถูกแทนแล้วยังอยู่ในรายการและยังเปิดได้** `SUPERSEDED` ไม่ใช่การลบ และ
 *    ประวัติ forecast คือสิ่งที่ผู้วางแผนใช้ตอบว่าทำไมแผนเดือนที่แล้วถึงเป็นอย่างนั้น
 *
 * การ gate สิทธิ์เป็นแบบเดียวกับทุกหน้าจอธุรกิจ: `PLANNING` + `VIEW` เป็นสิทธิ์ของ
 * **การเปิดหน้าจอนี้** (ด่านอยู่ที่ route) ส่วน `CREATE` (นำไฟล์เข้า) และ `APPROVE`
 * (ประกาศใช้) เป็นสิทธิ์ของ **ปุ่มที่เขียน** ซึ่งถูกบังคับข้างในนี้ทีละปุ่ม ตรงกับ
 * ด่านจริงฝั่งเซิร์ฟเวอร์ทีละเส้น
 *
 * ⚠️ ทั้งหมดนี้เป็น UX layer ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์เป็นผู้ตัดสินทุก request
 * เสมอ (`references/authorization-rbac.md` ข้อ 5)
 */

import { useCallback, useEffect, useState } from 'react';

import { StatusBadge } from '../StatusBadge';
import { Modal } from '../components/Modal';
import { InfoHint } from '../components/InfoHint';
import { can } from '../../lib/permissions/client';
import type { PermissionsState } from '../../lib/permissions/types';
import {
  createForecastImport,
  currentStatusFromFailure,
  fetchForecastImport,
  fetchForecastImports,
  publishForecastImport,
  ruleFromFailure,
  type ApiErrorEnvelope,
  type ForecastImportFailure,
} from '../../lib/forecasts/client';
import {
  EMPTY_FORECAST_LINE_DRAFT,
  FORECAST_LINE_ERROR_DESCRIPTORS,
  FORECAST_PUBLISH_ACTION,
  FORECAST_STATUS_DESCRIPTORS,
  canPublishForecastBatch,
  countFailedForecastLines,
  filterForecastImports,
  forecastBatchStatusLabel,
  forecastBatchStatusTone,
  forecastDateLabel,
  forecastLineErrorHint,
  forecastLineErrorLabel,
  isFailedForecastLine,
  toCreateForecastImportBody,
  toForecastStatusFilter,
  type CreateForecastImportFields,
  type ForecastLineDraft,
} from '../../lib/forecasts/forecastsView';
import {
  CUSTOMER_CODE_MAX_LENGTH,
  CUSTOMER_NOT_FOUND,
  DUPLICATE_LINE_NO,
  FORECAST_BATCH_IS_INVALID,
  FORECAST_BATCH_NOT_VALIDATED,
  FORECAST_IMPORT_FIELD_IS_SERVER_OWNED,
  INVALID_BATCH_STATUS,
  PLANNING_MODULE,
  PLANT_CODE_MAX_LENGTH,
  PLANT_OUTSIDE_SCOPE,
  PRODUCT_CODE_MAX_LENGTH,
  SOURCE_FILE_HASH_MAX_LENGTH,
  SUPERSEDED_BATCH_STATUS,
  UOM_CODE_MAX_LENGTH,
  VERSION_LABEL_MAX_LENGTH,
  type ForecastImportBatch,
  type ForecastImportBatchSummary,
  type ForecastLine,
} from '../../lib/forecasts/types';

/* ------------------------------------------------- หน้าจอความล้มเหลว ----- */

type ForecastFailureContext =
  /** อ่านรายการ batch */
  | 'batches'
  /** อ่านใบเดียวเพื่อดูบรรทัด */
  | 'detail'
  /** นำไฟล์เข้าระบบ */
  | 'create'
  /** ประกาศใช้ใบใดใบหนึ่ง */
  | 'publish';

interface FailureCopy {
  readonly heading: string;
  readonly text: string;
}

/**
 * ประโยค "ไม่มีอะไรเปลี่ยน" ของแต่ละ context ที่ **เขียน** — สตริงว่างสำหรับ context
 * ที่อ่านอย่างเดียว
 *
 * ของ `publish` ต้องพูดถึง **ใบเก่า** ด้วย: สิ่งที่การประกาศใช้ทำคือเปลี่ยนสถานะของ
 * ใบเดิมให้เป็น `SUPERSEDED` ในธุรกรรมเดียวกัน ผู้ใช้ที่ถูกปฏิเสธจึงต้องรู้ว่าใบเดิม
 * **ไม่ได้ถูกแตะ** ไม่งั้นเขาจะไปตรวจว่าแผนที่ใช้อยู่ยังถูกต้องหรือเปล่า
 */
function nothingChangedText(context: ForecastFailureContext): string {
  switch (context) {
    case 'create':
      return ' ไม่มี batch ใดถูกสร้าง ไม่มีบรรทัดใดถูกเขียน และไม่มีแถว audit ถูกเขียน';
    case 'publish':
      return ' ใบนี้ยังอยู่ในสถานะเดิม ใบที่ประกาศใช้อยู่ก่อนหน้าไม่ถูกแตะ และไม่มีแถว audit ถูกเขียน';
    case 'batches':
    case 'detail':
      return '';
  }
}

function isWriteContext(context: ForecastFailureContext): boolean {
  return nothingChangedText(context) !== '';
}

/**
 * ข้อความของกฎแต่ละข้อ — คืน `null` เมื่อไม่ใช่กฎที่ contract เขียนไว้
 *
 * ⚠️ ห้ามเพิ่มรหัสที่ contract ไม่ได้ระบุลงใน `switch` นี้ — รหัสที่หน้าจอคิดขึ้นเอง
 * คือข้อความที่ไม่มีวันแสดง และเป็นคำสัญญาที่ backend ไม่เคยให้ไว้
 *
 * ⚠️ และห้ามใส่ **token ราย line** (เช่น `PRODUCT_NOT_FOUND`) ลงที่นี่ด้วย: ห้าตัวนั้น
 * ไม่เคยเดินทางมาเป็นรหัสของคำตอบที่ล้มเหลว มันถูกเก็บไว้ **บนบรรทัด** ของใบที่สร้าง
 * สำเร็จ และถูกแสดงในตารางบรรทัด ไม่ใช่ในกล่องแจ้งความล้มเหลว
 */
function ruleCopy(rule: string | null): FailureCopy | null {
  switch (rule) {
    case FORECAST_BATCH_IS_INVALID:
      return {
        heading: 'ใบนี้มีบรรทัดที่ผิด จึงประกาศใช้ไม่ได้',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${FORECAST_BATCH_IS_INVALID} — **นี่คือด่านที่กันไม่ให้แถวที่ผิดกลายเป็นความต้องการผลิตเงียบ ๆ** (flow ข้อ 1.3) กด "ดูบรรทัด" ที่แถวนี้เพื่อดูว่าแถวไหนไม่ผ่านและเพราะอะไร แล้วแก้ไฟล์ของลูกค้าให้ถูกก่อนนำเข้าใหม่เป็น revision ถัดไป — การกดซ้ำจะได้คำตอบเดิม`,
      };
    case FORECAST_BATCH_NOT_VALIDATED:
      return {
        heading: 'ใบนี้ไม่ได้อยู่ในสถานะที่ประกาศใช้ได้แล้ว',
        text: `เซิร์ฟเวอร์ตอบ 409 พร้อมกฎ ${FORECAST_BATCH_NOT_VALIDATED} — **คนละเรื่องกับ "ใบมีแถวผิด"**: ใบนี้ไม่ได้ผิดอะไร มันแค่ผ่านจุดนั้นไปแล้ว (ประกาศใช้ไปแล้ว หรือถูกแทนด้วยฉบับใหม่ไปแล้ว) สาเหตุที่พบบ่อยที่สุดคือมีคนกดไปก่อนแล้วระหว่างที่หน้าจอนี้เปิดค้างอยู่ โหลดรายการใหม่เพื่อดูสถานะจริง`,
      };
    case PLANT_OUTSIDE_SCOPE:
      return {
        heading: 'โรงงานที่ระบุอยู่นอกขอบเขตของคุณ',
        text: `เซิร์ฟเวอร์ตอบ 403 พร้อมกฎ ${PLANT_OUTSIDE_SCOPE} — forecast หนึ่งชุดเป็นของโรงงานเดียว และคุณนำเข้าให้โรงงานที่ไม่ได้อยู่ในสิทธิ์เข้าถึงของตัวเองไม่ได้ (D-004) ตรวจรหัสโรงงาน หรือขอสิทธิ์เข้าถึงโรงงานนั้นเพิ่ม`,
      };
    case CUSTOMER_NOT_FOUND:
      return {
        heading: 'ไม่พบลูกค้ารหัสนี้',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${CUSTOMER_NOT_FOUND} — ไม่มีลูกค้ารหัสนี้ในทะเบียน ตรวจรหัสอีกครั้งจากหน้า "ลูกค้า (Customer Master)" — **นี่เป็นกฎของทั้งไฟล์ ไม่ใช่ของบรรทัดใดบรรทัดหนึ่ง** จึงไม่มีอะไรถูกเขียนเลย`,
      };
    case DUPLICATE_LINE_NO:
      return {
        heading: 'เลขบรรทัดซ้ำกันเองในไฟล์นี้',
        text: `เซิร์ฟเวอร์ตอบ 400 พร้อมกฎ ${DUPLICATE_LINE_NO} — สองบรรทัดถือเลขเดียวกัน จึงอ้างไม่ได้ว่าแถวไหนคือแถวไหนตอนต้องกลับไปแก้ไฟล์ แก้เลขบรรทัดที่ซ้ำให้ต่างกันแล้วส่งใหม่ — สิ่งที่กรอกไว้ยังอยู่ครบ`,
      };
    case FORECAST_IMPORT_FIELD_IS_SERVER_OWNED:
      return {
        heading: 'คำขอมีฟิลด์ที่เซิร์ฟเวอร์เป็นเจ้าของ',
        text: `เซิร์ฟเวอร์ตอบ 400 พร้อมกฎ ${FORECAST_IMPORT_FIELD_IS_SERVER_OWNED} — สถานะ ผู้สร้าง ผู้ประกาศใช้ และเวลา เป็นค่าที่เซิร์ฟเวอร์เติมเองทุกเส้นทาง กล่องนี้ประกอบคำขอทีละคีย์อยู่แล้วจึงส่งค่าเหล่านั้นไปไม่ได้ **การที่คำตอบนี้มาถึงคือบั๊ก** ไม่ใช่สิ่งที่คุณกรอกผิด — แจ้งผู้ดูแลระบบ`,
      };
    default:
      return null;
  }
}

/**
 * `409` ที่ไม่ได้มาพร้อมรหัสกฎ — อธิบายจาก `details.current_status` แทน
 *
 * contract สัญญาว่า `409` ของ publish พา `current_status` มาด้วยเสมอ และในกลุ่มนี้มัน
 * บอกสิ่งที่ผู้ใช้ต้องรู้จริง ๆ: ใบนี้ **อยู่ที่ไหน** จึงกดไม่ได้
 */
function conflictCopy(currentStatus: string | null): FailureCopy {
  return {
    heading:
      currentStatus === null
        ? 'สถานะปัจจุบันของใบนี้ไม่รับสิ่งที่กด'
        : `ใบนี้อยู่ที่ ${forecastBatchStatusLabel(currentStatus)} แล้ว`,
    text: `เซิร์ฟเวอร์ตอบ 409 —${
      currentStatus === null
        ? ''
        : ` สถานะที่เก็บอยู่จริงคือ ${forecastBatchStatusLabel(currentStatus)} ซึ่ง`
    } ประกาศใช้ได้เฉพาะใบที่ตรวจผ่านแล้วเท่านั้น สาเหตุที่พบบ่อยที่สุดคือมีคนกดไปก่อนแล้ว หรือมี revision ใหม่ของคู่โรงงานและลูกค้านี้ถูกประกาศใช้แทนไปแล้ว โหลดรายการใหม่เพื่อดูสถานะจริงก่อนตัดสินใจ — **การกดซ้ำจะได้คำตอบเดิม**`,
  };
}

/**
 * ข้อความประจำแต่ละสถานะ — แจกแจงครบโดยไม่มี `default` เพื่อให้ TypeScript เป็นผู้
 * พิสูจน์ว่าไม่มีสถานะไหนตกหล่นเมื่อ client เพิ่มกรณีใหม่
 */
function failureCopy(
  failure: ForecastImportFailure,
  context: ForecastFailureContext,
): FailureCopy {
  const writing = isWriteContext(context);
  const nothingChanged = nothingChangedText(context);
  const currentStatus = currentStatusFromFailure(failure);

  switch (failure.status) {
    case 'unauthenticated':
      return {
        heading: 'ต้องเข้าสู่ระบบก่อนจึงจะใช้หน้าจอนี้ได้',
        text: 'เซิร์ฟเวอร์ตอบ 401 — เซสชันหมดอายุหรือถูกยกเลิกไปแล้ว token ที่ถืออยู่จึงถูกล้างทิ้งและต้องเข้าสู่ระบบใหม่ (D-049)',
      };
    case 'forbidden':
      return (
        ruleCopy(failure.error.rule) ?? {
          heading: 'สิทธิ์ชุดนี้ไม่ครอบคลุมคำขอนี้',
          text: `เซิร์ฟเวอร์ตอบ 403 — คำขอนี้ต้องการโมดูล ${PLANNING_MODULE} ในระดับที่สูงกว่าที่แผนกของคุณถืออยู่ ขอสิทธิ์เพิ่มที่ตารางสิทธิ์ตามแผนก${nothingChanged}`,
        }
      );
    case 'not-found':
      return {
        heading: 'ไม่พบ forecast ที่คำขอนี้อ้างถึง',
        text: `เซิร์ฟเวอร์ตอบ 404 — ใบนี้ไม่มีอยู่แล้ว หรืออยู่ในโรงงานนอกขอบเขตของคุณ (สองกรณีนี้ตอบเหมือนกันโดยตั้งใจ ไม่งั้นคนที่ไซต์หนึ่งจะไล่เดาได้ว่าลูกค้ารายไหนส่งแผนมาให้อีกไซต์บ้าง) โหลดรายการใหม่แล้วลองอีกครั้ง${nothingChanged}`,
      };
    case 'conflict':
      return ruleCopy(failure.error.rule) ?? conflictCopy(currentStatus);
    case 'rule-violation':
      return (
        ruleCopy(failure.error.rule) ?? {
          heading: 'คำขอนี้ติดกฎทางธุรกิจ',
          text: `เซิร์ฟเวอร์ตอบ 422 พร้อมรหัสกฎที่ชน — ไม่ใช่ข้อผิดพลาดของระบบ แต่เป็นเงื่อนไขที่ต้องแก้ก่อน${nothingChanged}`,
        }
      );
    case 'bad-request':
      return (
        ruleCopy(failure.error.rule) ?? {
          heading: 'คำขอนี้ถูกปฏิเสธว่าไม่ถูกต้อง',
          text: `เซิร์ฟเวอร์ตอบ 400 — ข้อมูลที่ส่งไปไม่ตรงรูปแบบที่ API รับ (เช่น ชื่อ revision ยาวเกิน ${VERSION_LABEL_MAX_LENGTH} ตัวอักษร หรือวันที่ที่ส่งไปมีเวลาติดมาด้วยแทนที่จะเป็นวันตามปฏิทินล้วน)${nothingChanged}`,
        }
      );
    case 'server-error':
      return {
        heading: `เซิร์ฟเวอร์ตอบ ${failure.httpStatus}`,
        text: writing
          ? 'คำขอไปถึงเซิร์ฟเวอร์แล้วแต่ทำงานไม่สำเร็จ และ **ไม่มีอะไรยืนยันว่าข้อมูลถูกเปลี่ยนหรือไม่** — โหลดรายการใหม่เพื่อดูสถานะที่เก็บอยู่จริงก่อนลองอีกครั้ง'
          : 'คำขอไปถึงเซิร์ฟเวอร์แล้วแต่ทำงานไม่สำเร็จ ยังไม่มีข้อมูลจริงมาแสดง',
      };
    case 'network-error':
      return {
        heading: 'ติดต่อเซิร์ฟเวอร์ไม่ได้',
        text: writing
          ? `${failure.detail} — ไม่มีอะไรยืนยันว่าคำขอไปถึงเซิร์ฟเวอร์หรือไม่ โหลดรายการใหม่เพื่อดูสถานะที่เก็บอยู่จริงก่อนลองอีกครั้ง`
          : `${failure.detail} — หน้าจอนี้จึงไม่มีข้อมูลจะแสดง และจะไม่เดาขึ้นมาเอง`,
      };
    case 'invalid-response':
      return {
        heading: 'ข้อมูลจากเซิร์ฟเวอร์ผิดรูปแบบ',
        text: 'หน้าจอปฏิเสธที่จะแสดง forecast ที่อ่านได้ไม่ครบ — ไฟล์ที่ขาดไปหนึ่งบรรทัดคือแถวที่ผู้ใช้จะไม่เห็นว่าผิด แล้วเขาจะแก้ไฟล์ไม่ครบและได้คำตอบเดิมอีกครั้งโดยไม่เข้าใจว่าทำไม',
      };
    case 'cancelled':
      return {
        heading: 'ยกเลิกการเรียกข้อมูลแล้ว',
        text: 'คำขอถูกยกเลิกก่อนได้รับคำตอบ',
      };
  }
}

function envelopeOf(failure: ForecastImportFailure): ApiErrorEnvelope | null {
  return 'error' in failure ? failure.error : null;
}

export interface ForecastImportFailureNoticeProps {
  readonly failure: ForecastImportFailure;
  readonly context: ForecastFailureContext;
  readonly onRetry?: () => void;
}

export function ForecastImportFailureNotice({
  failure,
  context,
  onRetry,
}: ForecastImportFailureNoticeProps) {
  const copy = failureCopy(failure, context);
  const envelope = envelopeOf(failure);
  const rule = ruleFromFailure(failure);
  const serverMessage = envelope === null ? null : envelope.message;
  // `409` และ `422` เป็นคำตอบที่ **ผู้ใช้จัดการต่อเองได้** (แก้ไฟล์แล้วนำเข้าใหม่
  // หรือโหลดรายการใหม่) ต่างจากความล้มเหลวที่เหลือซึ่งเขาทำอะไรไม่ได้เลย
  const refusedByRule =
    failure.status === 'rule-violation' || failure.status === 'conflict';

  return (
    <div
      className="cap-notice"
      data-testid="forecast-failure"
      data-failure={failure.status}
      data-context={context}
      data-rule={rule ?? ''}
      role="status"
    >
      <StatusBadge tone={refusedByRule ? 'warning' : 'danger'}>
        {refusedByRule ? 'ติดกฎทางธุรกิจ' : 'ทำรายการไม่สำเร็จ'}
      </StatusBadge>
      <h3 className="cap-notice__title">{copy.heading}</h3>
      <p className="cap-notice__text">{copy.text}</p>

      {rule === null ? null : (
        <p className="cap-notice__text">
          กฎที่ชน:{' '}
          <code className="cap-table__code" data-testid="forecast-rule">
            {rule}
          </code>
        </p>
      )}

      {serverMessage === null ? null : (
        <p className="cap-notice__note" data-testid="forecast-failure-message">
          ข้อความจากเซิร์ฟเวอร์: {serverMessage}
        </p>
      )}

      {onRetry === undefined ? null : (
        <div className="cap-notice__actions">
          <button type="button" className="cap-button cap-button--ghost" onClick={onRetry}>
            ลองอีกครั้ง
          </button>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------- ตารางบรรทัดของหนึ่งใบ -- */

export interface ForecastLineTableProps {
  readonly batch: ForecastImportBatch;
  /** ใช้ประกอบ `data-testid` ให้แต่ละใบมีตารางของตัวเองที่อ้างถึงได้ */
  readonly testIdSuffix: string;
}

/**
 * บรรทัดทั้งหมดของใบหนึ่ง **พร้อมทำเครื่องหมายแถวที่ไม่ผ่าน** — ส่วนที่หน้าจอนี้มี
 * อยู่เพื่อทำ
 *
 * สามอย่างที่ต้องถูกต้องที่นี่ และทั้งสามคือกฎ ไม่ใช่การจัดหน้า:
 *
 * 1. **ทุกบรรทัดแสดง ไม่ใช่เฉพาะบรรทัดที่ผิด** — คนที่กำลังแก้ไฟล์ต้องเทียบแถวที่ผิด
 *    กับแถวข้าง ๆ ที่ผ่าน และเลขบรรทัดของทั้งสองต้องต่อกัน
 * 2. **แถวที่ผิดพกเหตุผลของตัวเองมาด้วย** ในภาษาที่คนอ่านออก ไม่ใช่ token ดิบ ๆ
 *    (แต่ token ก็ยังแสดงไว้ด้วย สำหรับตอนที่ต้องแจ้งผู้ดูแลระบบ)
 * 3. **`product_code` ที่เป็น `null` แสดงเป็น "—" ไม่ใช่ช่องว่าง** — มันคือรหัสที่
 *    resolve ไม่ได้ ซึ่งเป็นสาเหตุของความผิดในแถวนั้นพอดี ช่องว่างอ่านเหมือนตาราง
 *    ที่เรนเดอร์ไม่ครบ
 */
export function ForecastLineTable({ batch, testIdSuffix }: ForecastLineTableProps) {
  const failedCount = countFailedForecastLines(batch.lines);

  return (
    <div className="cap-table-wrap">
      <table className="cap-table" data-testid={`forecast-lines-${testIdSuffix}`}>
        <caption className="cap-visually-hidden">
          บรรทัดทั้งหมดของ {batch.version_label} พร้อมเหตุผลของแถวที่ไม่ผ่าน
        </caption>
        <thead>
          <tr>
            <th scope="col">บรรทัดที่</th>
            <th scope="col">รหัสสินค้า</th>
            <th scope="col">วันที่</th>
            <th scope="col">จำนวน</th>
            <th scope="col">หน่วย</th>
            <th scope="col">ผลการตรวจ</th>
          </tr>
        </thead>
        <tbody>
          {batch.lines.map((line: ForecastLine) => {
            const failed = isFailedForecastLine(line);
            const label = forecastLineErrorLabel(line.validation_error);
            const hint = forecastLineErrorHint(line.validation_error);

            return (
              <tr
                key={line.line_no}
                data-testid={`forecast-line-${testIdSuffix}-${line.line_no}`}
                data-failed={failed ? 'true' : 'false'}
                data-validation-error={line.validation_error ?? ''}
              >
                <th scope="row" className="cap-table__row-head">
                  <code className="cap-table__code">{line.line_no}</code>
                </th>
                <td>
                  {line.product_code === null ? (
                    <span className="cap-table__muted">—</span>
                  ) : (
                    <code className="cap-table__code">{line.product_code}</code>
                  )}
                </td>
                <td>
                  {/* `YYYY-MM-DD` ตามตัวอักษร — ไม่เดินทางผ่าน `Date` เลย (D-093) */}
                  <code className="cap-table__code">
                    {forecastDateLabel(line.forecast_date)}
                  </code>
                </td>
                <td>{line.quantity}</td>
                <td>
                  <code className="cap-table__code">{line.uom_code}</code>
                </td>
                <td>
                  {failed ? (
                    <>
                      <StatusBadge tone="danger">{label}</StatusBadge>
                      {hint === null ? null : (
                        <p className="cap-table__muted">{hint}</p>
                      )}
                      <p className="cap-table__muted">
                        กฎที่ชน:{' '}
                        <code className="cap-table__code">{line.validation_error}</code>
                      </p>
                    </>
                  ) : (
                    <StatusBadge tone="success">ผ่าน</StatusBadge>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="cap-card__hint" data-testid={`forecast-lines-summary-${testIdSuffix}`}>
        {failedCount === 0
          ? `ทั้ง ${batch.lines.length} บรรทัดผ่านการตรวจ`
          : `มี ${failedCount} จาก ${batch.lines.length} บรรทัดที่ไม่ผ่าน — แก้แถวเหล่านี้ในไฟล์ของลูกค้าแล้วนำเข้าใหม่เป็น revision ถัดไป ใบนี้ยังอยู่ในระบบเป็นหลักฐานว่าไฟล์ฉบับนี้มีอะไรผิด`}
      </p>
    </div>
  );
}

/* --------------------------------------------------------------- หน้าจอ -- */

export interface ForecastImportScreenProps {
  readonly permissions: PermissionsState;
  readonly apiBaseUrl: string;
  /** inject ได้เพื่อการทดสอบ — production ใช้ `fetch` ของเบราว์เซอร์ */
  readonly fetchImpl?: typeof fetch;
}

type BatchesState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly items: readonly ForecastImportBatchSummary[] }
  | ForecastImportFailure;

/**
 * ใบที่ผู้ใช้กดดูบรรทัด — **หนึ่งใบเท่านั้นในแต่ละครั้ง**
 *
 * รูปนี้เป็นเหตุผลที่ contract ตัดบรรทัดออกจากรายการได้ตั้งแต่แรก: ไฟล์ forecast
 * หนึ่งไฟล์มีหลายร้อยบรรทัด และคนอ่านทีละใบ
 */
type DetailState =
  | { readonly status: 'loading'; readonly batchId: string }
  | { readonly status: 'ready'; readonly batch: ForecastImportBatch }
  | {
      readonly status: 'failed';
      readonly batchId: string;
      readonly failure: ForecastImportFailure;
    };

/** คำขอที่กำลังเดินทางอยู่ — ใช้ปิดปุ่มทั้งหน้าจอระหว่างนั้น */
type Pending =
  | { readonly kind: 'create' }
  | { readonly kind: 'publish'; readonly batchId: string };

/** ผลของปุ่มประกาศใช้ที่ผูกกับแถวที่กด — ทั้งสำเร็จและไม่สำเร็จอยู่ที่แถวนั้น */
interface PublishOutcome {
  readonly batchId: string;
  readonly failure: ForecastImportFailure | null;
  readonly batch: ForecastImportBatch | null;
}

const EMPTY_CREATE_FIELDS: CreateForecastImportFields = {
  plantCode: '',
  customerCode: '',
  versionLabel: '',
  sourceFileHash: '',
};

/**
 * หนึ่งแถวของ line editor — `key` เป็นของ React ไม่ได้เดินทางไปไหน
 *
 * ใช้ key ที่นับขึ้นเรื่อย ๆ **ไม่ใช่ index ของ array**: index จะถูกใช้ซ้ำทันทีที่ลบ
 * แถวกลางออก แล้วค่าที่พิมพ์ไว้ในแถวถัด ๆ ไปจะเลื่อนตำแหน่งตามกันไปทั้งแถบ (วันที่
 * ของบรรทัดหนึ่งไปโผล่คู่กับสินค้าของอีกบรรทัด ซึ่งในกลุ่มนี้คือ forecast ที่ตกผิดวัน)
 */
interface CreateLineRow {
  readonly key: number;
  readonly draft: ForecastLineDraft;
}

function newLineRow(key: number): CreateLineRow {
  return { key, draft: EMPTY_FORECAST_LINE_DRAFT };
}

export function ForecastImportScreen({
  permissions,
  apiBaseUrl,
  fetchImpl,
}: ForecastImportScreenProps) {
  const canCreate = can(permissions, PLANNING_MODULE, 'CREATE');
  const canApprove = can(permissions, PLANNING_MODULE, FORECAST_PUBLISH_ACTION);

  const [batches, setBatches] = useState<BatchesState>({ status: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);
  const [pending, setPending] = useState<Pending | null>(null);

  const [query, setQuery] = useState('');
  /** ตัวกรองสถานะ — สตริงว่างคือ "ทุกสถานะ" และเดินทางไปเป็น `?status=` ที่เซิร์ฟเวอร์ */
  const [statusFilter, setStatusFilter] = useState('');

  const [detail, setDetail] = useState<DetailState | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [createFields, setCreateFields] =
    useState<CreateForecastImportFields>(EMPTY_CREATE_FIELDS);
  const [createLines, setCreateLines] = useState<readonly CreateLineRow[]>([newLineRow(0)]);
  const [nextLineKey, setNextLineKey] = useState(1);
  const [createFailure, setCreateFailure] = useState<ForecastImportFailure | null>(null);
  const [createdBatch, setCreatedBatch] = useState<ForecastImportBatch | null>(null);

  const [publishOutcome, setPublishOutcome] = useState<PublishOutcome | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    // ระหว่างโหลดซ้ำหลังกดปุ่มสำเร็จ **ตารางเดิมยังอยู่** ไม่กระพริบเป็นหน้าว่าง
    setBatches((previous) =>
      previous.status === 'ready' ? previous : { status: 'loading' },
    );

    const options = { apiBaseUrl, fetchImpl, signal: controller.signal };
    const status = toForecastStatusFilter(statusFilter);

    void fetchForecastImports(status === undefined ? {} : { status }, options).then(
      (result) => {
        if (!active || result.status === 'cancelled') {
          return;
        }
        setBatches(
          result.status === 'ready' ? { status: 'ready', items: result.page.items } : result,
        );
      },
    );

    return () => {
      active = false;
      controller.abort();
    };
  }, [apiBaseUrl, fetchImpl, reloadToken, statusFilter]);

  /**
   * ดูบรรทัดของใบเดียว — `GET /{batch_id}` **ตอนที่ผู้ใช้กดดู** ไม่ใช่ตอนโหลดรายการ
   *
   * นี่คือครึ่งที่สองของการตัดบรรทัดออกจากรายการ: รายการเบาเพราะไม่มีบรรทัด และ
   * บรรทัดมาถึงเมื่อมีคนต้องการอ่านมันจริง ๆ **หนึ่งคำขอต่อหนึ่งใบที่เปิดดู** ไม่ใช่
   * หนึ่งคำขอต่อทุกแถวในตาราง
   *
   * ⚠️ ใบที่ `SUPERSEDED` เปิดได้เหมือนใบอื่นทุกประการ ไม่มีเงื่อนไขพิเศษที่นี่ —
   * flow ข้อ 1.4 เก็บมันไว้เพื่อให้คนตอบได้ว่าทำไมแผนฉบับก่อนถึงเป็นแบบนั้น
   */
  const openDetail = async (batch: ForecastImportBatchSummary) => {
    setDetail({ status: 'loading', batchId: batch.batch_id });
    const result = await fetchForecastImport(batch.batch_id, { apiBaseUrl, fetchImpl });
    if (result.status === 'cancelled') {
      return;
    }
    setDetail(
      result.status === 'ready'
        ? { status: 'ready', batch: result.batch }
        : { status: 'failed', batchId: batch.batch_id, failure: result },
    );
  };

  const openCreate = () => {
    if (!canCreate) {
      return;
    }
    // เปิดกล่องใหม่ = เริ่มใหม่: ผลของครั้งก่อน (สำเร็จหรือถูกปฏิเสธ) ไม่ใช่ผลของครั้งนี้
    setCreateFields(EMPTY_CREATE_FIELDS);
    setCreateLines([newLineRow(nextLineKey)]);
    setNextLineKey((key) => key + 1);
    setCreateFailure(null);
    setCreatedBatch(null);
    setCreateOpen(true);
  };

  /** อ้างอิงคงที่ — `Modal` ผูก/ถอน listener ของ Esc ตาม `onClose` */
  const closeCreate = useCallback(() => {
    setCreateOpen(false);
  }, []);

  const updateField = (field: keyof CreateForecastImportFields, value: string) => {
    setCreateFields((previous) => ({ ...previous, [field]: value }));
  };

  const addLine = () => {
    setCreateLines((previous) => [...previous, newLineRow(nextLineKey)]);
    setNextLineKey((key) => key + 1);
  };

  const removeLine = (key: number) => {
    setCreateLines((previous) => previous.filter((row) => row.key !== key));
  };

  const updateLine = (key: number, field: keyof ForecastLineDraft, value: string) => {
    setCreateLines((previous) =>
      previous.map((row) =>
        row.key === key ? { ...row, draft: { ...row.draft, [field]: value } } : row,
      ),
    );
  };

  const lineDrafts: readonly ForecastLineDraft[] = createLines.map((row) => row.draft);

  const submitCreate = async () => {
    const body = toCreateForecastImportBody(createFields, lineDrafts);
    if (body === null || pending !== null || !canCreate) {
      return;
    }

    setPending({ kind: 'create' });
    setCreateFailure(null);
    setCreatedBatch(null);

    const result = await createForecastImport(body, { apiBaseUrl, fetchImpl });
    setPending(null);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      // **ไม่แตะรายการเลย และไม่ปิดกล่อง** — สิ่งที่กรอกไว้ทั้งหัวใบและทุกบรรทัดยังอยู่
      // ให้แก้แล้วส่งใหม่ได้ทันที
      setCreateFailure(result);
      return;
    }

    // **`ready` ไม่ได้แปลว่าไฟล์ถูกต้อง** มันแปลว่าไฟล์ถูกบันทึกและตรวจแล้ว — ผลการ
    // ตรวจอยู่ใน `status` และใน `lines[].validation_error` ซึ่งถูกแสดงเต็ม ๆ ด้านล่าง
    setCreatedBatch(result.batch);
    setCreateOpen(false);
    // คำค้นที่ค้างอยู่ถูกล้างทิ้ง ไม่งั้นใบที่เพิ่งนำเข้า (ซึ่งมักไม่ตรงกับคำค้นเดิม)
    // จะถูกกรองออกทันทีที่โหลดเสร็จ
    setQuery('');
    setReloadToken((token) => token + 1);
  };

  /**
   * ประกาศใช้ — **การกระทำที่สองและเป็นคนละการกระทำกับการนำเข้า**
   *
   * ด่านสองชั้นก่อนคำขอจะออกเดินทาง และทั้งสองชั้นตรงกับด่านจริงฝั่งเซิร์ฟเวอร์:
   * สถานะต้องเป็น `VALIDATED` (ไม่งั้น `409`/`422`) และผู้กดต้องถือ APPROVE
   * (ไม่งั้น `403`)
   */
  const runPublish = async (batch: ForecastImportBatchSummary) => {
    if (
      pending !== null ||
      !canApprove ||
      !canPublishForecastBatch(batch.status)
    ) {
      return;
    }

    setPending({ kind: 'publish', batchId: batch.batch_id });
    setPublishOutcome(null);

    const result = await publishForecastImport(batch.batch_id, { apiBaseUrl, fetchImpl });

    setPending(null);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      // **ไม่แตะรายการเลย** — คำอธิบายอยู่ที่แถวที่เขากด ไม่ใช่ลอยอยู่ท้ายหน้า
      setPublishOutcome({ batchId: batch.batch_id, failure: result, batch: null });
      return;
    }

    setPublishOutcome({ batchId: batch.batch_id, failure: null, batch: result.batch });
    // สถานะใหม่ของ **ทั้งคู่** (ใบนี้และใบที่เพิ่งถูกแทน) มาจากการโหลดรายการใหม่
    // ไม่ใช่การแก้แถวในหน่วยความจำ — การ supersede เกิดกับใบที่หน้าจอไม่ได้กด
    setReloadToken((token) => token + 1);
  };

  const items = batches.status === 'ready' ? batches.items : [];
  const matchedItems = filterForecastImports(items, query);
  const busy = pending !== null;
  const createBody = toCreateForecastImportBody(createFields, lineDrafts);
  /** ปุ่มที่เขียนได้สักปุ่มหนึ่ง — ใช้ตัดสินว่าจะขึ้นป้าย "ดูได้อย่างเดียว" ไหม */
  const canWriteAnything = canCreate || canApprove;

  /** แถวนี้เสนอปุ่มประกาศใช้ได้จริงไหม = สถานะที่กดได้ ∩ สิทธิ์ที่ผู้ใช้ถือ */
  const offersPublish = (batch: ForecastImportBatchSummary): boolean =>
    canApprove && canPublishForecastBatch(batch.status);

  const anyRowHasActions = matchedItems.some(offersPublish);

  return (
    <div className="cap-page" data-testid="forecast-imports">
      <div className="cap-page__head">
        <div>
          <h1 className="cap-page__title">
            Forecast ลูกค้า (Forecast Import){' '}
            <InfoHint
              label="เกี่ยวกับหน้านี้"
              text={
                <>
                  แผนความต้องการรายวันที่ลูกค้าส่งมา — <strong>สองขั้นตอน</strong>:
                  นำไฟล์เข้าแล้วระบบตรวจทุกบรรทัด จากนั้นผู้รับผิดชอบ
                  <strong>ตรวจแล้วจึงประกาศใช้</strong> (D-093) ไฟล์ที่มีบรรทัดผิดจะถูก
                  เก็บไว้ทั้งชุดให้ดูว่า<strong>แถวไหนผิดและเพราะอะไร</strong> — ไม่มีแถว
                  ใดถูกทิ้งเงียบ ๆ และยังไม่มีความต้องการผลิตใดเกิดขึ้นจนกว่าจะประกาศใช้
                </>
              }
            />
          </h1>
        </div>
      </div>

      {!canWriteAnything ? (
        <div className="cap-notice" data-testid="forecast-read-only" role="status">
          <StatusBadge tone="warning">ดูได้อย่างเดียว</StatusBadge>
          <h2 className="cap-notice__title">
            สิทธิ์ชุดนี้ยังนำไฟล์เข้าหรือประกาศใช้ไม่ได้
          </h2>
          <p className="cap-notice__text">
            สิทธิ์ที่เซิร์ฟเวอร์ส่งมาไม่มี flag{' '}
            <code className="cap-table__code">can_create</code> หรือ{' '}
            <code className="cap-table__code">can_approve</code> ของโมดูล{' '}
            <code className="cap-table__code">{PLANNING_MODULE}</code> ปุ่ม
            &quot;นำ Forecast เข้าระบบ&quot; และปุ่มประกาศใช้ในแต่ละแถวจึงไม่แสดง —
            รายการ ช่องค้นหา ตัวกรองสถานะ และการดูบรรทัดพร้อมเหตุผลของแถวที่ผิด
            ยังใช้ได้ตามปกติ
          </p>
          <p className="cap-notice__note">
            การซ่อนปุ่มเป็นเพียง UX เพื่อไม่ให้กดสิ่งที่จะถูกปฏิเสธ — ผู้ตัดสินจริงคือ
            backend ทุกคำขอเสมอ
          </p>
        </div>
      ) : null}

      {/* --------------------------------- ค้นหา + ตัวกรอง + ปุ่มนำเข้า -- */}

      <div className="cap-filters" data-testid="forecast-toolbar">
        <label className="cap-field">
          <span className="cap-field__label">
            ค้นหา Forecast{' '}
            <InfoHint
              label="เกี่ยวกับช่องค้นหา Forecast"
              text="กรองรายการด้านล่างจากชื่อ revision หรือรหัสลูกค้า — กรองจากรายการที่โหลดมาแล้ว ไม่ได้ถามเซิร์ฟเวอร์ใหม่ ช่องว่างหมายถึงแสดงทุกใบ"
            />
          </span>
          <input
            className="cap-input"
            type="search"
            name="forecast_query"
            data-testid="forecast-search"
            value={query}
            autoComplete="off"
            onChange={(event) => {
              setQuery(event.target.value);
            }}
          />
        </label>

        <label className="cap-field">
          <span className="cap-field__label">
            สถานะ{' '}
            <InfoHint
              label="เกี่ยวกับตัวกรองสถานะ"
              text="ต่างจากช่องค้นหา — ตัวกรองนี้ถามเซิร์ฟเวอร์ใหม่ด้วย ?status= จึงครอบคลุมใบทุกใบ ไม่ใช่เฉพาะหน้าที่โหลดมาแล้ว"
            />
          </span>
          <select
            className="cap-input"
            name="status_filter"
            data-testid="forecast-status-filter"
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value);
            }}
          >
            <option value="">ทุกสถานะ</option>
            {FORECAST_STATUS_DESCRIPTORS.map((descriptor) => (
              <option key={descriptor.code} value={descriptor.code}>
                {descriptor.label}
              </option>
            ))}
          </select>
        </label>

        {canCreate ? (
          <button
            type="button"
            className="cap-button cap-button--primary"
            data-testid="forecast-create-open"
            onClick={openCreate}
          >
            นำ Forecast เข้าระบบ
          </button>
        ) : null}
      </div>

      {/* --------------------------------------------------------- นำเข้า -- */}

      <Modal
        open={canCreate && createOpen}
        onClose={closeCreate}
        title="นำ Forecast ของลูกค้าเข้าระบบ"
      >
        <form
          className="cap-form"
          data-testid="forecast-create-form"
          aria-label="นำ Forecast ของลูกค้าเข้าระบบ"
          onSubmit={(event) => {
            event.preventDefault();
            void submitCreate();
          }}
        >
          <label className="cap-field">
            <span className="cap-field__label">
              รหัสโรงงาน{' '}
              <InfoHint
                label="เกี่ยวกับรหัสโรงงาน"
                text="ต้องเป็นโรงงานที่คุณมีสิทธิ์เข้าถึง — forecast หนึ่งชุดเป็นของโรงงานเดียว (D-004)"
              />
            </span>
            <input
              className="cap-input"
              name="plant_code"
              data-testid="forecast-create-plant"
              value={createFields.plantCode}
              maxLength={PLANT_CODE_MAX_LENGTH}
              autoComplete="off"
              onChange={(event) => {
                updateField('plantCode', event.target.value);
              }}
            />
          </label>

          <label className="cap-field">
            <span className="cap-field__label">
              รหัสลูกค้า{' '}
              <InfoHint
                label="เกี่ยวกับรหัสลูกค้า"
                text={
                  <>
                    บังคับกรอก — การประกาศใช้จะแทน forecast ฉบับก่อนของ{' '}
                    <strong>คู่ (โรงงาน, ลูกค้า) นี้</strong>
                  </>
                }
              />
            </span>
            <input
              className="cap-input"
              name="customer_code"
              data-testid="forecast-create-customer"
              value={createFields.customerCode}
              maxLength={CUSTOMER_CODE_MAX_LENGTH}
              autoComplete="off"
              onChange={(event) => {
                updateField('customerCode', event.target.value);
              }}
            />
          </label>

          <label className="cap-field">
            <span className="cap-field__label">
              ชื่อ revision ของไฟล์{' '}
              <InfoHint
                label="เกี่ยวกับชื่อ revision"
                text={
                  <>
                    ชื่อที่ <strong>ลูกค้า</strong> ตั้งไว้บนไฟล์ (เช่น{' '}
                    <code className="cap-table__code">2026-W38-rev2</code>) ยาวไม่เกิน{' '}
                    {VERSION_LABEL_MAX_LENGTH} ตัวอักษร — ไม่ใช่เลขเอกสารของเรา และ
                    <strong>ซ้ำกันได้</strong> เพราะลูกค้ารายเดิมส่งไฟล์แก้เข้ามาได้เรื่อย ๆ
                  </>
                }
              />
            </span>
            <input
              className="cap-input"
              name="version_label"
              data-testid="forecast-create-version"
              value={createFields.versionLabel}
              maxLength={VERSION_LABEL_MAX_LENGTH}
              autoComplete="off"
              onChange={(event) => {
                updateField('versionLabel', event.target.value);
              }}
            />
          </label>

          <label className="cap-field">
            <span className="cap-field__label">
              ค่าตรวจสอบไฟล์ต้นทาง (source file hash, ไม่บังคับ){' '}
              <InfoHint
                label="เกี่ยวกับค่าตรวจสอบไฟล์ต้นทาง"
                text={
                  <>
                    เก็บไว้เพื่อการตรวจสอบย้อนหลังว่า batch นี้มาจากไฟล์ฉบับไหน —{' '}
                    <strong>ไม่ใช่ตัวกันการนำเข้าซ้ำ</strong>
                  </>
                }
              />
            </span>
            <input
              className="cap-input"
              name="source_file_hash"
              data-testid="forecast-create-hash"
              value={createFields.sourceFileHash}
              maxLength={SOURCE_FILE_HASH_MAX_LENGTH}
              autoComplete="off"
              onChange={(event) => {
                updateField('sourceFileHash', event.target.value);
              }}
            />
          </label>

          {/* ------------------------------------------- บรรทัดของไฟล์ -- */}

          <fieldset className="cap-field" data-testid="forecast-create-lines">
            <legend className="cap-field__label">
              บรรทัดในไฟล์ Forecast{' '}
              <InfoHint
                label="เกี่ยวกับบรรทัดในไฟล์ Forecast"
                text={
                  <>
                    คีย์ทุกบรรทัดที่อยู่ในไฟล์ของลูกค้า —{' '}
                    <strong>วันที่เป็นวันตามปฏิทิน ไม่ใช่ช่วงเวลา</strong>: forecast คือ
                    ปริมาณของ <em>วัน</em> หนึ่ง สินค้าตัวเดียวกันของวันเดียวกันจึงมีได้
                    แถวเดียว ต้องมีอย่างน้อยหนึ่งบรรทัดจึงจะนำเข้าได้
                  </>
                }
              />
            </legend>

            <div className="cap-table-wrap">
              <table className="cap-table" data-testid="forecast-create-lines-table">
                <caption className="cap-visually-hidden">
                  บรรทัดที่จะนำเข้าเป็น Forecast พร้อมปุ่มลบรายบรรทัด
                </caption>
                <thead>
                  <tr>
                    <th scope="col">บรรทัดที่</th>
                    <th scope="col">รหัสสินค้า</th>
                    <th scope="col">วันที่ (Forecast Date)</th>
                    <th scope="col">จำนวน</th>
                    <th scope="col">หน่วย</th>
                    <th scope="col">ลบ</th>
                  </tr>
                </thead>
                <tbody>
                  {createLines.map((row, index) => (
                    /**
                     * `key` เป็น **รหัสของแถว** (ไม่ถูกใช้ซ้ำแม้ลบแถวกลางออก) ส่วน
                     * `data-testid`/`name`/`aria-label` เป็น **ลำดับที่ผู้ใช้เห็น**
                     */
                    <tr key={row.key} data-testid={`forecast-create-line-${index + 1}`}>
                      <td>
                        <input
                          className="cap-input"
                          name={`line_no_${index + 1}`}
                          data-testid={`forecast-create-line-no-${index + 1}`}
                          aria-label={`เลขบรรทัดในไฟล์ แถวที่ ${index + 1}`}
                          value={row.draft.lineNo}
                          inputMode="numeric"
                          autoComplete="off"
                          disabled={busy}
                          onChange={(event) => {
                            updateLine(row.key, 'lineNo', event.target.value);
                          }}
                        />
                      </td>
                      <td>
                        <input
                          className="cap-input"
                          name={`product_code_${index + 1}`}
                          data-testid={`forecast-create-line-product-${index + 1}`}
                          aria-label={`รหัสสินค้า แถวที่ ${index + 1}`}
                          value={row.draft.productCode}
                          maxLength={PRODUCT_CODE_MAX_LENGTH}
                          autoComplete="off"
                          disabled={busy}
                          onChange={(event) => {
                            updateLine(row.key, 'productCode', event.target.value);
                          }}
                        />
                      </td>
                      <td>
                        <input
                          className="cap-input"
                          type="date"
                          name={`forecast_date_${index + 1}`}
                          data-testid={`forecast-create-line-date-${index + 1}`}
                          aria-label={`วันที่ของ forecast แถวที่ ${index + 1}`}
                          value={row.draft.forecastDate}
                          disabled={busy}
                          onChange={(event) => {
                            updateLine(row.key, 'forecastDate', event.target.value);
                          }}
                        />
                      </td>
                      <td>
                        <input
                          className="cap-input"
                          name={`quantity_${index + 1}`}
                          data-testid={`forecast-create-line-quantity-${index + 1}`}
                          aria-label={`จำนวน แถวที่ ${index + 1}`}
                          value={row.draft.quantity}
                          inputMode="decimal"
                          autoComplete="off"
                          disabled={busy}
                          onChange={(event) => {
                            updateLine(row.key, 'quantity', event.target.value);
                          }}
                        />
                      </td>
                      <td>
                        <input
                          className="cap-input"
                          name={`uom_code_${index + 1}`}
                          data-testid={`forecast-create-line-uom-${index + 1}`}
                          aria-label={`หน่วยนับ แถวที่ ${index + 1}`}
                          value={row.draft.uomCode}
                          maxLength={UOM_CODE_MAX_LENGTH}
                          autoComplete="off"
                          disabled={busy}
                          onChange={(event) => {
                            updateLine(row.key, 'uomCode', event.target.value);
                          }}
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="cap-button cap-button--ghost"
                          data-testid={`forecast-create-line-remove-${index + 1}`}
                          aria-label={`ลบบรรทัดที่ ${index + 1}`}
                          disabled={busy}
                          onClick={() => {
                            removeLine(row.key);
                          }}
                        >
                          ลบบรรทัด
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <button
              type="button"
              className="cap-button cap-button--ghost"
              data-testid="forecast-create-line-add"
              disabled={busy}
              onClick={addLine}
            >
              เพิ่มบรรทัด
            </button>
          </fieldset>

          <div className="cap-form__actions">
            <button
              type="submit"
              className="cap-button cap-button--primary"
              data-testid="forecast-create-submit"
              disabled={busy || createBody === null}
            >
              {pending?.kind === 'create' ? 'กำลังนำเข้า…' : 'นำเข้าและตรวจ'}
            </button>
            <button
              type="button"
              className="cap-button cap-button--ghost"
              data-testid="forecast-create-cancel"
              disabled={busy}
              onClick={closeCreate}
            >
              ยกเลิก
            </button>
          </div>

          <p className="cap-field__hint" data-testid="forecast-create-summary">
            มี {createLines.length} บรรทัด
            {createBody === null
              ? ' — ปุ่มนำเข้ายังกดไม่ได้จนกว่าหัวไฟล์จะครบ และทุกบรรทัดจะมีเลขบรรทัด สินค้า วันที่ตามปฏิทิน จำนวนที่ไม่ติดลบ (ศูนย์ใช้ได้) และหน่วยนับครบ'
              : ' — การนำเข้ายังไม่สร้างความต้องการผลิต ต้องมีคนตรวจแล้วกดประกาศใช้อีกขั้นหนึ่ง'}
          </p>

          {/**
           * ความล้มเหลวอยู่ **ในกล่อง** ข้างฟอร์มที่ทำให้มันเกิด — ผู้ใช้แก้แล้วกดส่ง
           * ใหม่ได้ทันทีโดยไม่ต้องเปิดกล่องใหม่และคีย์บรรทัดใหม่ทั้งหมด
           */}
          {createFailure === null ? null : (
            <ForecastImportFailureNotice failure={createFailure} context="create" />
          )}
        </form>
      </Modal>

      {/* ---------------------------------------------- ผลของการนำเข้า -- */}

      {createdBatch === null ? null : (
        <div className="cap-notice" data-testid="forecast-create-result" role="status">
          <StatusBadge tone={forecastBatchStatusTone(createdBatch.status)}>
            {forecastBatchStatusLabel(createdBatch.status)}
          </StatusBadge>
          <h2 className="cap-notice__title">
            นำไฟล์ {createdBatch.version_label} เข้าระบบแล้ว
          </h2>
          <p className="cap-notice__text">
            ลูกค้า <code className="cap-table__code">{createdBatch.customer_code}</code>{' '}
            โรงงาน <code className="cap-table__code">{createdBatch.plant_code}</code>{' '}
            จำนวน {createdBatch.lines.length} บรรทัด —{' '}
            {createdBatch.status === INVALID_BATCH_STATUS ? (
              <strong>
                ไฟล์นี้มีบรรทัดที่ไม่ผ่าน จึงประกาศใช้ไม่ได้ ดูตารางด้านล่างว่าแถวไหนผิด
                แล้วแก้ไฟล์ของลูกค้าก่อนนำเข้าใหม่
              </strong>
            ) : (
              <strong>
                ตรวจผ่านทุกบรรทัดแล้ว แต่ <em>ยังไม่เป็นความต้องการผลิต</em> — ต้องมี
                ผู้รับผิดชอบตรวจแล้วกด &quot;ประกาศใช้&quot; ในรายการด้านล่างอีกขั้นหนึ่ง
                (flow ข้อ 1.4)
              </strong>
            )}
          </p>
          <ForecastLineTable batch={createdBatch} testIdSuffix="created" />
        </div>
      )}

      {/* ------------------------------------------- ผลของปุ่มประกาศใช้ -- */}

      {publishOutcome?.batch == null ? null : (
        <div className="cap-notice" data-testid="forecast-publish-result" role="status">
          <StatusBadge tone="success">ประกาศใช้แล้ว</StatusBadge>
          <h2 className="cap-notice__title">
            {publishOutcome.batch.version_label} อยู่ที่สถานะ{' '}
            {forecastBatchStatusLabel(publishOutcome.batch.status)} แล้ว
          </h2>
          <p className="cap-notice__text">
            forecast ฉบับที่ประกาศใช้อยู่ก่อนหน้าของคู่ (โรงงาน, ลูกค้า) นี้ถูกตั้งเป็น{' '}
            {forecastBatchStatusLabel(SUPERSEDED_BATCH_STATUS)} ในธุรกรรมเดียวกัน —{' '}
            <strong>ไม่ได้ถูกลบและไม่ได้ถูกเขียนทับ</strong> มันยังอยู่ในรายการด้านล่าง
            และยังเปิดอ่านได้ (flow ข้อ 1.4) สถานะนี้มาจากคำตอบของเซิร์ฟเวอร์ ไม่ใช่จาก
            สิ่งที่หน้าจอเพิ่งส่งไป และรายการถูกโหลดใหม่ทั้งหน้าเพื่อให้เห็นใบที่ถูกแทนด้วย
          </p>
        </div>
      )}

      {/* ---------------------------------------------------- รายการ batch -- */}

      {batches.status === 'loading' ? (
        <div className="cap-notice" data-testid="forecast-loading" role="status">
          <p className="cap-notice__text">กำลังโหลดรายการ Forecast…</p>
        </div>
      ) : null}

      {batches.status !== 'loading' && batches.status !== 'ready' ? (
        <ForecastImportFailureNotice
          failure={batches}
          context="batches"
          onRetry={() => {
            setReloadToken((token) => token + 1);
          }}
        />
      ) : null}

      {batches.status === 'ready' && items.length === 0 ? (
        <div className="cap-notice" data-testid="forecast-empty" role="status">
          <StatusBadge tone="neutral">ยังไม่มี Forecast</StatusBadge>
          <h2 className="cap-notice__title">
            {statusFilter === ''
              ? 'ยังไม่มี Forecast ในโรงงานที่คุณเข้าถึงได้'
              : `ไม่มี Forecast ที่สถานะเป็น ${forecastBatchStatusLabel(statusFilter)}`}
          </h2>
          <p className="cap-notice__text">
            เซิร์ฟเวอร์ตอบสำเร็จแต่รายการว่างเปล่า — ไม่ใช่การโหลดที่ล้มเหลว
            {statusFilter === '' ? '' : ' เลือก "ทุกสถานะ" เพื่อดูใบทั้งหมด'}
          </p>
        </div>
      ) : null}

      {/**
       * "หาไม่เจอ" ไม่ใช่ "ยังไม่มี Forecast" และไม่ใช่ "โหลดไม่สำเร็จ" — ทั้งสามให้
       * หน้าจอที่ไม่มีตารางเหมือนกัน แต่สิ่งที่ผู้ใช้ต้องทำต่อคนละเรื่องกันโดยสิ้นเชิง
       */}
      {batches.status === 'ready' && items.length > 0 && matchedItems.length === 0 ? (
        <div className="cap-notice" data-testid="forecast-no-match" role="status">
          <StatusBadge tone="neutral">ไม่พบใบที่ค้นหา</StatusBadge>
          <h2 className="cap-notice__title">
            ไม่มี Forecast ที่ตรงกับ &quot;{query.trim()}&quot;
          </h2>
          <p className="cap-notice__text">
            รายการนี้มี Forecast {items.length} ใบ แต่ไม่มีใบใดที่ชื่อ revision หรือรหัส
            ลูกค้ามีคำนี้อยู่ — ล้างคำค้นเพื่อดูทั้งหมด
          </p>
        </div>
      ) : null}

      {batches.status === 'ready' && matchedItems.length > 0 ? (
        <section className="cap-card cap-card--table" aria-label="Forecast ทั้งหมด">
          <div className="cap-card__head">
            <h2 className="cap-card__title">Forecast ทั้งหมด</h2>
            <span className="cap-card__hint">
              {query.trim() === ''
                ? 'เรียงตามที่เซิร์ฟเวอร์ส่งมา'
                : `แสดง ${matchedItems.length} จาก ${items.length} ใบที่ตรงกับคำค้น`}
            </span>
          </div>
          <div className="cap-table-wrap">
            <table className="cap-table" data-testid="forecast-table">
              <caption className="cap-visually-hidden">
                Forecast ของโรงงานที่คุณเข้าถึงได้ พร้อมสถานะและปุ่มที่สถานะนั้นอนุญาต —
                ใบที่ถูกแทนด้วยฉบับใหม่ยังอยู่ในรายการนี้และยังเปิดดูได้
              </caption>
              <thead>
                <tr>
                  <th scope="col">ชื่อ revision</th>
                  <th scope="col">โรงงาน</th>
                  <th scope="col">ลูกค้า</th>
                  <th scope="col">สถานะ</th>
                  <th scope="col">จำนวนบรรทัด</th>
                  <th scope="col">นำเข้าเมื่อ</th>
                  <th scope="col">บรรทัด</th>
                  {anyRowHasActions ? <th scope="col">การดำเนินการ</th> : null}
                </tr>
              </thead>
              <tbody>
                {matchedItems.map((batch) => {
                  const outcome =
                    publishOutcome?.batchId === batch.batch_id &&
                    publishOutcome.failure !== null
                      ? publishOutcome
                      : null;
                  const openedDetail =
                    detail !== null &&
                    (detail.status === 'ready'
                      ? detail.batch.batch_id === batch.batch_id
                      : detail.batchId === batch.batch_id)
                      ? detail
                      : null;

                  return (
                    <tr
                      key={batch.batch_id}
                      data-testid={`forecast-row-${batch.version_label}`}
                      data-status={batch.status}
                    >
                      <th scope="row" className="cap-table__row-head">
                        <code className="cap-table__code">{batch.version_label}</code>
                      </th>
                      <td>
                        <code className="cap-table__code">{batch.plant_code}</code>
                      </td>
                      <td>
                        <code className="cap-table__code">{batch.customer_code}</code>
                      </td>
                      <td>
                        <StatusBadge tone={forecastBatchStatusTone(batch.status)}>
                          {forecastBatchStatusLabel(batch.status)}
                        </StatusBadge>
                      </td>
                      <td data-testid={`forecast-line-count-${batch.version_label}`}>
                        {batch.line_count === null ? (
                          <span className="cap-table__muted">—</span>
                        ) : (
                          batch.line_count
                        )}
                      </td>
                      <td>
                        {batch.created_at === null ? (
                          <span className="cap-table__muted">—</span>
                        ) : (
                          <code className="cap-table__code">{batch.created_at}</code>
                        )}
                      </td>
                      <td>
                        {/**
                         * **บรรทัดไม่ได้มากับรายการ** (contract ตัดออก) ปุ่มนี้จึงยิง
                         * `GET /{batch_id}` ของ **ใบนี้ใบเดียว** ตอนที่มีคนอยากอ่านมัน
                         * จริง ไม่ใช่ตอนโหลดตาราง — และมันเปิดได้ทุกสถานะ รวมทั้ง
                         * `SUPERSEDED` ซึ่งเป็นใบที่ flow ข้อ 1.4 เก็บไว้ให้ตอบคำถาม
                         * ว่าทำไมแผนฉบับก่อนถึงเป็นอย่างนั้น
                         */}
                        <button
                          type="button"
                          className="cap-button cap-button--ghost"
                          data-testid={`forecast-detail-open-${batch.version_label}`}
                          disabled={openedDetail?.status === 'loading'}
                          onClick={() => {
                            void openDetail(batch);
                          }}
                        >
                          {openedDetail?.status === 'loading'
                            ? 'กำลังโหลด…'
                            : 'ดูบรรทัด'}
                        </button>

                        {openedDetail?.status === 'ready' ? (
                          <ForecastLineTable
                            batch={openedDetail.batch}
                            testIdSuffix={batch.version_label}
                          />
                        ) : null}

                        {openedDetail?.status === 'failed' ? (
                          <ForecastImportFailureNotice
                            failure={openedDetail.failure}
                            context="detail"
                          />
                        ) : null}
                      </td>
                      {anyRowHasActions ? (
                        <td>
                          {/**
                           * **ปุ่มเดียวของ lifecycle และมันขึ้นเฉพาะใบที่ `VALIDATED`**
                           * ใบที่ `INVALID` ไม่มี affordance ให้กดเลย ไม่ใช่มีปุ่มที่
                           * กดแล้วถูกปฏิเสธ — ส่วน `422 FORECAST_BATCH_IS_INVALID`
                           * ฝั่งเซิร์ฟเวอร์ยังเป็นด่านจริงเสมอสำหรับทางเข้าอื่น
                           */}
                          {offersPublish(batch) ? (
                            <button
                              type="button"
                              className="cap-button cap-button--ghost"
                              data-testid={`forecast-publish-${batch.version_label}`}
                              disabled={busy}
                              onClick={() => {
                                void runPublish(batch);
                              }}
                            >
                              {pending?.kind === 'publish' &&
                              pending.batchId === batch.batch_id
                                ? 'กำลังส่ง…'
                                : 'ประกาศใช้'}
                            </button>
                          ) : null}

                          {/**
                           * คำอธิบายอยู่ **ที่แถวที่ถูกปฏิเสธ** ไม่ใช่ลอยอยู่ท้ายหน้า
                           */}
                          {outcome === null || outcome.failure === null ? null : (
                            <div data-testid="forecast-publish-failure-slot">
                              <ForecastImportFailureNotice
                                failure={outcome.failure}
                                context="publish"
                              />
                            </div>
                          )}
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="cap-card__hint">
            สถานะที่ปุ่มอ้างถึง{' '}
            <InfoHint
              label="ความหมายของแต่ละสถานะ"
              text={FORECAST_STATUS_DESCRIPTORS.map(
                (descriptor) => `${descriptor.label} — ${descriptor.summary}`,
              ).join(' / ')}
            />
          </p>
          <p className="cap-card__hint" data-testid="forecast-line-error-legend">
            เหตุผลที่บรรทัดหนึ่งไม่ผ่าน{' '}
            <InfoHint
              label="ความหมายของแต่ละเหตุผล"
              text={FORECAST_LINE_ERROR_DESCRIPTORS.map(
                (descriptor) => `${descriptor.label} (${descriptor.token})`,
              ).join(' / ')}
            />
          </p>
        </section>
      ) : null}
    </div>
  );
}
